import React, { useContext } from "react"
import { observer } from "mobx-react"
import { Anchor, Button, Group, Paper, Stack, Text, Title } from "@mantine/core"
import {
  IconArrowLeft,
  IconArrowRight,
  IconBrandGithub,
  IconExternalLink,
  IconMarkdown,
  IconRocket
} from "@tabler/icons-react"
import { AppWorkspaceContext } from "../../stores/AppWorkspace"
import {
  CATALOG,
  LANDING_COPY,
  PLATFORM_KIND_ORDER,
  PLATFORMS,
  PLATFORMS_INDEX,
  platformHeadline
} from "../../../docs/catalog"
import { guideBySlug } from "../../../docs/lazy"
import type { Platform } from "../../../docs/types"
import { FRAMEWORKS_HASH } from "../../helpers/constants"
import { MarkdownDoc } from "../MarkdownDoc"
import { useNearViewport } from "../../hooks/useNearViewport"
import { CopyPromptButton } from "./CopyPromptButton"
import { MobileHandoff } from "../MobileHandoff"
import { PlatformCard } from "./PlatformCard"
import { TestedBadge } from "./TestedBadge"
import { SiteVideo } from "../SiteVideo"
import { platformVideo } from "../../helpers/siteVideo"

// /platforms and /platforms/<id> (contract v8). The index is a marketing page
// and the proof behind it: how we test, then every row grouped by kind, with
// planned rows as "Coming next". A platform page is a header drawn from its
// row above the guide the row names, rendered like any guide. PlatformPage
// (Rails) renders the same facts into the server body for crawlers.
export const PlatformsPage: React.FC = observer(() => {
  const workspace = useContext(AppWorkspaceContext)
  if (!workspace.platformId) return <PlatformsIndexPage />
  if (!workspace.platform) return <UnknownPlatform id={workspace.platformId} />
  return <PlatformDetail platform={workspace.platform} />
})

const PlatformsIndexPage: React.FC = observer(() => {
  const workspace = useContext(AppWorkspaceContext)
  const copy = LANDING_COPY.platforms
  const methodPath = PLATFORMS_INDEX?.methodGuidePath ?? null
  const groups = PLATFORM_KIND_ORDER.map((kind) => ({
    kind,
    label: copy.kinds[kind] ?? kind,
    rows: PLATFORMS.filter((platform) => platform.kind === kind)
  })).filter((group) => group.rows.length > 0)

  return (
    <Stack gap={40} py="md" pb="xl" className="or-platforms-index">
      <div>
        <Text className="or-eyebrow">{copy.eyebrow}</Text>
        <Title order={1} className="or-integration-title or-platforms-index-title">
          {copy.indexHeadline}
        </Title>
        <Text className="or-lede or-integration-lede">
          {copy.indexMethod}{" "}
          {methodPath ? (
            <Anchor
              href={methodPath}
              onClick={(event) => {
                event.preventDefault()
                workspace.openHref(methodPath)
              }}
            >
              {copy.indexMethodLink} <IconArrowRight size={14} aria-hidden="true" />
            </Anchor>
          ) : null}
        </Text>
      </div>

      {groups.map((group) => (
        <section key={group.kind} className="or-frameworks" aria-labelledby={`or-platforms-${group.kind}`}>
          <Title order={2} id={`or-platforms-${group.kind}`} className="or-frameworks-title">
            {group.label}
          </Title>
          <ul className="or-framework-cards">
            {group.rows.map((platform) => (
              <PlatformCard key={platform.id} platform={platform} />
            ))}
          </ul>
        </section>
      ))}

      <Text c="dimmed">
        {copy.frameworksPointer}{" "}
        <Anchor
          href={`/#${FRAMEWORKS_HASH}`}
          onClick={(event) => {
            event.preventDefault()
            workspace.goHome(FRAMEWORKS_HASH)
          }}
        >
          {copy.frameworksPointerLink} <IconArrowRight size={14} aria-hidden="true" />
        </Anchor>
      </Text>
    </Stack>
  )
})

// The server answered 404 for this id already (a planned row or a typo); this
// is only what the SPA draws if it navigated here client-side.
const UnknownPlatform: React.FC<{ id: string }> = observer(({ id }) => {
  const workspace = useContext(AppWorkspaceContext)
  return (
    <Stack gap="md" py="xl">
      <Title order={1}>No platform page for “{id}”</Title>
      <Group>
        <Button variant="light" onClick={() => workspace.goPlatforms()}>
          All platforms
        </Button>
      </Group>
    </Stack>
  )
})

const PlatformDetail: React.FC<{ platform: Platform }> = observer(({ platform }) => {
  const workspace = useContext(AppWorkspaceContext)
  const logo = workspace.platformLogoUrl(platform.id)
  // The guide sits below the video, and its markdown comes in the chunk that
  // carries every guide's (~300 KB gzipped): fetched as the reader nears it,
  // or at once for a #hash that points into it.
  const [guideRef, guideNear] = useNearViewport<HTMLDivElement>()
  const guide = guideNear || workspace.hash ? guideBySlug(platform.guideSlug) : null
  // Two rows can share one guide. The one that has a prompt (an AI builder)
  // leads with Copy prompt; the other leads with its starter's deploy button.
  const leadWithPrompt = Boolean(platform.prompt)

  const starterButton = platform.starter ? (
    <Button
      component="a"
      href={platform.starter.url}
      target="_blank"
      rel="noopener noreferrer"
      size="md"
      variant={leadWithPrompt ? "default" : "filled"}
      leftSection={<IconRocket size={18} />}
      rightSection={<IconExternalLink size={14} />}
    >
      {platform.starter.label}
    </Button>
  ) : null
  const promptButton = platform.prompt ? (
    <CopyPromptButton prompt={platform.prompt} platformId={platform.id} label={platform.label} size="md" variant="filled" />
  ) : null

  type Fact = { label: string; value: React.ReactNode }
  const facts = ([
    platform.frameworkLabel
      ? {
          label: "Runs on",
          value: platform.frameworkPath ? (
            <Anchor
              href={platform.frameworkPath}
              onClick={(event) => {
                event.preventDefault()
                workspace.openHref(platform.frameworkPath as string)
              }}
            >
              {platform.frameworkLabel}
            </Anchor>
          ) : (
            platform.frameworkLabel
          )
        }
      : null,
    platform.database ? { label: "Database", value: platform.database } : null,
    platform.secrets ? { label: "Secrets", value: platform.secrets } : null,
    platform.settlement ? { label: "Settlement", value: platform.settlement } : null
  ] as (Fact | null)[]).filter((fact): fact is Fact => fact !== null)

  const video = platformVideo(platform.id)

  return (
    <Stack gap={40} className="or-integration or-platform-page" pb="xl">
      <section className="or-platform-hero">
        <Button
          variant="subtle"
          size="compact-sm"
          leftSection={<IconArrowLeft size={14} />}
          onClick={() => workspace.goPlatforms()}
          mb="md"
        >
          All platforms
        </Button>
        {logo ? (
          <span className="or-framework-logo or-integration-logo or-platform-logo" data-platform={platform.id}>
            <img src={logo} alt={`${platform.label} logo`} />
          </span>
        ) : null}
        {/* The kind, not the name: the logo and the headline already say it. */}
        <Text className="or-eyebrow">{LANDING_COPY.platforms.kinds[platform.kind] ?? platform.kind}</Text>
        <Title order={1} className="or-integration-title">
          {platformHeadline(platform)}
        </Title>
        <Text className="or-lede or-integration-lede">{platform.summary}</Text>
        <TestedBadge tested={platform.tested} className="or-tested-badge-hero" />
        <Group className="or-hero-actions" gap="sm" mt="lg">
          {leadWithPrompt ? promptButton : starterButton}
          {leadWithPrompt ? starterButton : promptButton}
          {platform.starter ? (
            <Button
              component="a"
              href={platform.starter.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              size="md"
              variant="default"
              leftSection={<IconBrandGithub size={18} />}
            >
              Starter source
            </Button>
          ) : null}
        </Group>
        <MobileHandoff name={platform.label} shortPath={`/${platform.id}`} pageKind="platform" pageId={platform.id} />
      </section>

      {facts.length ? (
        <Paper className="or-platform-facts" radius="lg" p={{ base: "md", sm: "xl" }} withBorder>
          <Title order={2} className="or-quicksetup-title">
            How it runs
          </Title>
          <dl className="or-platform-facts-list">
            {facts.map((fact) => (
              <div key={fact.label} className="or-platform-fact">
                <dt>{fact.label}</dt>
                <dd>{fact.value}</dd>
              </div>
            ))}
          </dl>
        </Paper>
      ) : null}

      <SiteVideo
        video={video}
        title={
          video === "explainer"
            ? "What OpenReceive does, in one video"
            : `Watch payments added to a ${platform.label} app`
        }
        label={
          video === "explainer"
            ? "What OpenReceive is and how it works"
            : `OpenReceive added to a ${platform.label} app`
        }
        sourceLocation="platform"
        className="or-integration-video"
      />

      <div ref={guideRef}>
        {guide ? (
          <article className="or-platform-guide">
            <Group justify="space-between" align="flex-start" mb="md">
              <div>
                <Text className="or-eyebrow">Guide</Text>
                <Title order={2}>{guide.title}</Title>
              </div>
              <Group gap="xs">
                {platform.markdownPath ? (
                  <Button
                    component="a"
                    href={platform.markdownPath}
                    variant="default"
                    size="xs"
                    leftSection={<IconMarkdown size={14} />}
                    title="This guide as raw markdown, for a coding agent"
                  >
                    Markdown
                  </Button>
                ) : null}
                <Button
                  component="a"
                  href={guide.githubUrl}
                  variant="default"
                  size="xs"
                  target="_blank"
                  rel="noopener noreferrer"
                  leftSection={<IconExternalLink size={14} />}
                >
                  Source
                </Button>
              </Group>
            </Group>
            <MarkdownDoc markdown={guide.markdown.replace(/^# [^\n]+\n+/, "")} />
            <Text size="xs" c="dimmed" mt="xl">
              Synced from {guide.sourcePath} · OpenReceive {CATALOG.sourceVersion}
            </Text>
          </article>
        ) : null}
      </div>
    </Stack>
  )
})
